import { Expose } from "class-transformer";
import { Locale as DatePickerLocale } from "date-fns/locale";
import { CountryCode } from "@airhelp/flags";
import { getLocaleLibrary } from "components/ui-elements/itinerary-flight-details/arrived/getFormattedDelayTime";
import { LocaleDirection } from "utils/getLocaleDirection";
import { type ParseKeys as TranslationKey } from "i18next";

export type Locale =
  | "ar"
  | "da"
  | "de"
  | "en"
  | "es"
  | "el"
  | "fr"
  | "it"
  | "nl"
  | "nb"
  | "pl"
  | "pt-BR"
  | "pt"
  | "ru"
  | "ro"
  | "fi"
  | "sv"
  | "zh-CN"
  | "tr";

export type Currency = keyof typeof currencies;

export const currencies = Object.freeze({
  EUR: {
    code: "EUR",
    symbol: "€",
    displayName: "EUR Euro",
    icon: "eu" as CountryCode,
  },
  USD: {
    code: "USD",
    currencySymbol: "$",
    displayName: "USD US Dollar",
    icon: "us" as CountryCode,
  },
  BRL: {
    code: "BRL",
    currencySymbol: "R$",
    displayName: "Brazil Real",
    icon: "br" as CountryCode,
  },
  GBP: {
    code: "GBP",
    currencySymbol: "£",
    displayName: "Pound sterling",
    icon: "gb" as CountryCode,
  },
  CAD: {
    code: "CAD",
    currencySymbol: "CA$",
    displayName: "Canadian dollar",
    icon: "ca" as CountryCode,
  },
});

export type AhpCurrency = "EUR" | "USD" | "BRL" | "PLN" | "SEK" | "CHF" | "GBP" | "RON";

export interface AhpCurrencySettings {
  id: AhpCurrency;
  name: TranslationKey;
  icon: CountryCode;
  code: AhpCurrency;
  symbol: string;
  symbolPosition: "before" | "after";
}

export const ahPlusCurrencies: Record<AhpCurrency, AhpCurrencySettings> = Object.freeze({
  EUR: {
    id: "EUR",
    name: "steps.common.currency.eur",
    icon: "eu",
    code: "EUR",
    symbol: "€",
    symbolPosition: "before",
  },
  USD: {
    id: "USD",
    name: "steps.common.currency.usd",
    icon: "us",
    code: "USD",
    symbol: "$",
    symbolPosition: "before",
  },
  BRL: {
    id: "BRL",
    name: "steps.common.currency.brl",
    icon: "br",
    code: "BRL",
    symbol: "R$",
    symbolPosition: "before",
  },
  PLN: {
    id: "PLN",
    name: "steps.common.currency.pln",
    icon: "pl",
    code: "PLN",
    symbol: "zł",
    symbolPosition: "after",
  },
  SEK: {
    id: "SEK",
    name: "steps.common.currency.sek",
    icon: "se",
    code: "SEK",
    symbol: "kr",
    symbolPosition: "after",
  },
  CHF: {
    id: "CHF",
    name: "steps.common.currency.chf",
    icon: "ch",
    code: "CHF",
    symbol: "CHF",
    symbolPosition: "before",
  },
  GBP: {
    id: "GBP",
    name: "steps.common.currency.gbp",
    icon: "gb",
    code: "GBP",
    symbol: "£",
    symbolPosition: "before",
  },
  RON: {
    id: "RON",
    name: "steps.common.currency.ron",
    icon: "ro",
    code: "RON",
    symbol: "lei",
    symbolPosition: "after",
  },
});

export type LocaleSettings = {
  code: Locale;
  displayName: string;
  icon: CountryCode;
  dir: LocaleDirection;
};

export const locales: Record<Locale, LocaleSettings> = Object.freeze({
  ar: {
    code: "ar",
    displayName: "العربية",
    icon: "sa",
    dir: "rtl",
  },
  da: {
    code: "da",
    displayName: "Dansk",
    icon: "dk",
    dir: "ltr",
  },
  de: {
    code: "de",
    displayName: "Deutsch",
    icon: "de",
    dir: "ltr",
  },
  en: {
    code: "en",
    displayName: "English",
    icon: "gb",
    dir: "ltr",
  },
  es: {
    code: "es",
    displayName: "Español",
    icon: "es",
    dir: "ltr",
  },
  el: {
    code: "el",
    displayName: "Ελληνικά",
    icon: "gr",
    dir: "ltr",
  },
  fr: {
    code: "fr",
    displayName: "Français",
    icon: "fr",
    dir: "ltr",
  },
  it: {
    code: "it",
    displayName: "Italiano",
    icon: "it",
    dir: "ltr",
  },
  nl: {
    code: "nl",
    displayName: "Nederlands",
    icon: "nl",
    dir: "ltr",
  },
  nb: {
    code: "nb",
    displayName: "Norsk",
    icon: "no",
    dir: "ltr",
  },
  pl: {
    code: "pl",
    displayName: "Polski",
    icon: "pl",
    dir: "ltr",
  },
  "pt-BR": {
    code: "pt-BR",
    displayName: "Português (Brasil)",
    icon: "br",
    dir: "ltr",
  },
  pt: {
    code: "pt",
    displayName: "Português (Portugal)",
    icon: "pt",
    dir: "ltr",
  },
  ru: {
    code: "ru",
    displayName: "Русский",
    icon: "ru",
    dir: "ltr",
  },
  ro: {
    code: "ro",
    displayName: "Română",
    icon: "ro",
    dir: "ltr",
  },
  fi: {
    code: "fi",
    displayName: "Suomi",
    icon: "fi",
    dir: "ltr",
  },
  sv: {
    code: "sv",
    displayName: "Svenska",
    icon: "se",
    dir: "ltr",
  },
  tr: {
    code: "tr",
    displayName: "Türkçe",
    icon: "tr",
    dir: "ltr",
  },
  "zh-CN": {
    code: "zh-CN",
    displayName: "简体中文",
    icon: "cn",
    dir: "ltr",
  },
} as const);

export const mapLocaleToDatePickerLocale = async (locale: Locale): Promise<DatePickerLocale> => {
  const localeLibrary = await getLocaleLibrary(locale);
  return localeLibrary;
};

export const mapLocaleToFlagKey = (locale: Locale): CountryCode => {
  const localeToFlagKeyMap: Record<Locale, CountryCode> = {
    ar: "sa",
    da: "dk",
    de: "de",
    en: "gb",
    es: "es",
    el: "gr",
    fr: "fr",
    it: "it",
    nl: "nl",
    nb: "no",
    pl: "pl",
    "pt-BR": "br",
    pt: "pt",
    ru: "ru",
    ro: "ro",
    fi: "fi",
    sv: "se",
    "zh-CN": "cn",
    tr: "tr",
  };
  return localeToFlagKeyMap[locale];
};

export class CompensationInCurrency {
  @Expose({ name: "eur" }) EUR?: number;
  @Expose({ name: "usd" }) USD?: number;
  @Expose({ name: "brl" }) BRL?: number;
  @Expose({ name: "gbp" }) GBP?: number;
  @Expose({ name: "cad" }) CAD?: number;
}

export const normalizeLocale = (locale: string): Locale => {
  const lowerCasedLocale = locale.toLowerCase();
  for (const key in locales) {
    if (key.toLocaleLowerCase() === lowerCasedLocale) {
      return key as Locale;
    }
  }
  if (lowerCasedLocale.includes("-")) {
    const localePrefix = lowerCasedLocale.split("-")[0];
    for (const key in locales) {
      if (key === localePrefix) {
        return key as Locale;
      }
    }
  }

  if (lowerCasedLocale.startsWith("zh")) {
    return "zh-CN" as Locale;
  }
  return "en";
};

export const getLocaleWithCountry = (locale?: string): string => {
  if (!locale) {
    return "en-US";
  }

  const specialCases: Record<string, string> = {
    "pt-BR": "pt-BR",
    "zh-CN": "zh-CN",
    ar: "ar-SA",
  };

  if (specialCases[locale]) {
    return specialCases[locale];
  }

  const languageToCountry: Record<string, string> = {
    en: "GB",
    de: "DE",
    es: "ES",
    fr: "FR",
    it: "IT",
    nl: "NL",
    nb: "NO",
    pl: "PL",
    pt: "PT",
    ru: "RU",
    ro: "RO",
    fi: "FI",
    sv: "SE",
    tr: "TR",
    da: "DK",
    el: "GR",
  };

  if (!languageToCountry[locale]) {
    return `en-US`;
  }

  return `${locale}-${languageToCountry[locale]}`;
};

export const mapBrowserLanguageToLocale = (locale: string): Locale => {
  const browserLanguageToLocale: { [key: string]: Locale } = {
    ar: "ar",
    da: "da",
    "da-DA": "da",
    de: "de",
    "de-DE": "de",
    el: "el",
    "el-EL": "el",
    es: "es",
    "es-ES": "es",
    fi: "fi",
    "fi-FI": "fi",
    fr: "fr",
    "fr-FR": "fr",
    it: "it",
    "it-IT": "it",
    nb: "nb",
    "nb-NB": "nb",
    nl: "nl",
    "nl-NL": "nl",
    ro: "ro",
    "ro-RO": "ro",
    ru: "ru",
    "ru-RU": "ru",
    sv: "sv",
    "sv-SV": "sv",
    pl: "pl",
    "pl-PL": "pl",
    pt: "pt",
    "pt-PT": "pt",
    "pt-br": "pt-BR",
    "pt-BR": "pt-BR",
    en: "en",
    "en-US": "en",
    "en-GB": "en",
    zh: "zh-CN",
    "zh-cn": "zh-CN",
    "zh-CN": "zh-CN",
    "zh-Hans-CN": "zh-CN",
    tr: "tr",
    "tr-TR": "tr",
  };

  return browserLanguageToLocale[locale] as Locale;
};
